import { AbstractVirtualManifestBuilder } from './AbstractVirtualManifestBuilder';
import { EditorElement, InlineElement } from '@wix/component-protocol/schema';
import { EditorPointer } from '@wix/editor-document-logic-api';
import type {
  CompRef,
  PopulatedComponentContainerInfo,
  PopulatedComponentContainerInfoMap,
} from '@wix/document-services-types';
import { DataType } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { ManifestComponentExtensionType } from '../stores';
import {
  findArrayItemsSelectedIndexKey,
  getCachedArrayItemIndex,
} from '../componentsPlatformServices/ArrayItemsPlatformService/handlers';
import { compact, get, isArray, last, values } from 'lodash';
import { DependencyToken, type IDependencyAPI } from '../dependencies';

type DependencyAPI<TToken extends DependencyToken> = Extract<
  IDependencyAPI,
  { type: TToken }
>['api'];
type DataDefinitions = NonNullable<(EditorElement | InlineElement)['data']>;
type DataDefinition = DataDefinitions[string];

type DataItemKeyInfo = {
  value: string;
  parts: string[];
  rootKey: string;
  leafKey: string;
  remainingParts: string[];
};

type SlotContext = {
  dataDefinitions: DataDefinitions;
  dataItemKeyInfo: DataItemKeyInfo;
  rootDataItem: DataDefinition;
};

function parseDataItemKey(dataItemKey: string): DataItemKeyInfo {
  const parts = dataItemKey.split('.').filter(Boolean);
  const rootKey = parts[0] ?? '';
  const leafKey = parts[parts.length - 1] ?? '';

  return {
    value: dataItemKey,
    parts,
    rootKey,
    leafKey,
    remainingParts: parts.slice(1),
  };
}

function canResolvePathPrefix(
  source: unknown,
  pathParts: readonly string[],
): boolean {
  const prefixParts = pathParts.slice(0, -1);
  if (prefixParts.length === 0) {
    return true;
  }

  return get(source, prefixParts) != null;
}

function getArrayItemsData(
  data: NonNullable<
    ReturnType<DependencyAPI<DependencyToken.BUILDER_DATA_GET>>
  >,
  rootDataItemKey: string,
): readonly unknown[] {
  const value = get(data, rootDataItemKey);
  return isArray(value) ? value : [];
}

function getArrayItemId(item: unknown): string | undefined {
  const id = get(item, '_id');
  return typeof id === 'string' ? id : undefined;
}

function findArrayItemIndexForSlot(
  slot: PopulatedComponentContainerInfo,
  rootDataItemKey: string,
  arrayItemsData: readonly unknown[],
): number | null {
  const arrayPathSegment = slot.nestedDataPath.find(
    (segment) =>
      segment.type === 'array' &&
      segment.key === rootDataItemKey &&
      segment._id !== undefined,
  );

  if (!arrayPathSegment) {
    return null;
  }

  const itemIndex = arrayItemsData.findIndex(
    (item) => getArrayItemId(item) === arrayPathSegment._id,
  );

  return itemIndex >= 0 ? itemIndex : null;
}

function pickActiveArrayItemSlot(
  matchingSlots: readonly PopulatedComponentContainerInfo[],
  rootDataItemKey: string,
  arrayItemsIndex: number | null,
  arrayItemsData: readonly unknown[],
): PopulatedComponentContainerInfo | null {
  if (typeof arrayItemsIndex !== 'number') {
    return null;
  }

  return (
    matchingSlots.find(
      (slot) =>
        findArrayItemIndexForSlot(slot, rootDataItemKey, arrayItemsData) ===
        arrayItemsIndex,
    ) ?? null
  );
}

function getSlotDataItemKey(slot: PopulatedComponentContainerInfo): string {
  return compact([
    ...slot.nestedDataPath.map((segment) => segment.key),
    slot.slotName,
  ]).join('.');
}

function getMatchingSlotsByDataItemKey(
  builderSlots: PopulatedComponentContainerInfoMap,
  dataItemKeyInfo: DataItemKeyInfo,
): PopulatedComponentContainerInfo[] {
  const slots = values(builderSlots);
  const fullPathMatches = slots.filter(
    (slot) => getSlotDataItemKey(slot) === dataItemKeyInfo.value,
  );

  if (fullPathMatches.length > 0) {
    return fullPathMatches;
  }

  const leafMatches = slots.filter(
    (slot) => slot.slotName === dataItemKeyInfo.leafKey,
  );

  return leafMatches.length === 1 ? leafMatches : [];
}

class MergeVirtualManifestBuilderPure extends AbstractVirtualManifestBuilder {
  reduceManifestEntity: AbstractVirtualManifestBuilder['reduceManifestEntity'] =
    (manifestProp, left, right, mappings) => {
      const valueA = left.value;
      const valueB = right.value;
      const mergedValue = { ...valueA, ...valueB };
      const mergedMappings = { ...mappings };

      if (valueB) {
        const keys = Object.keys(valueB);

        for (const key of keys) {
          const existingMappings = mappings?.[key];
          const rightKeyMappings = right.mappings?.[key] ?? [];

          mergedMappings[key] = existingMappings
            ? [...existingMappings, ...rightKeyMappings]
            : [...rightKeyMappings];
        }
      }

      return {
        value: mergedValue,
        mappings: mergedMappings,
      };
    };
}

export abstract class AbstractVirtualManifestBuilderWithActionableSlots extends AbstractVirtualManifestBuilder {
  protected extractSlotsEditorPointers(
    editorPointer: EditorPointer<CompRef>,
    editorElement: EditorElement | InlineElement,
  ): EditorPointer<CompRef> | null {
    const extensionType =
      this.manifestStore.getExtensionTypeByEditorPointer(editorPointer);

    if (extensionType === ManifestComponentExtensionType.SYSTEM) {
      return this.#extractSystemSlotEditorPointer(editorPointer);
    }

    const dataDefinitions = editorElement.data;
    const dataItemKey =
      editorElement.actions?.container?.execution?.data?.dataItemKey;

    if (!dataDefinitions || !dataItemKey) {
      return null;
    }

    if (!this.#hasComponentSlots(this.#getDocumentPointer(editorPointer))) {
      return null;
    }

    const dataItemKeyInfo = parseDataItemKey(dataItemKey);
    const rootDataItem = dataDefinitions[dataItemKeyInfo.rootKey];

    if (!rootDataItem) {
      return null;
    }

    const context = {
      dataDefinitions,
      dataItemKeyInfo,
      rootDataItem,
    };

    if (this.#shouldUseBuilderSlotsAPI(extensionType)) {
      return this.#extractBuilderSlotEditorPointer(
        editorPointer,
        editorElement,
        context,
      );
    }

    return this.#extractSlotEditorPointer(
      editorPointer,
      editorElement,
      context,
    );
  }

  #extractSystemSlotEditorPointer(
    editorPointer: EditorPointer<CompRef>,
  ): EditorPointer<CompRef> | null {
    const getChildren = this.dependenciesContributor.get(
      DependencyToken.COMPONENTS_CHILDREN_GET,
    );
    const children = getChildren(this.#getDocumentPointer(editorPointer));

    return this.#buildEditorPointer(children[0]);
  }

  #extractSlotEditorPointer(
    editorPointer: EditorPointer<CompRef>,
    editorElement: EditorElement | InlineElement,
    context: SlotContext,
  ): EditorPointer<CompRef> | null {
    switch (context.rootDataItem.dataType) {
      case DataType.container:
        return this.#extractContainerSlotEditorPointer(editorPointer, context);

      case DataType.arrayItems:
        return this.#extractArrayItemsSlotEditorPointer(
          editorPointer,
          editorElement,
          context.dataItemKeyInfo,
        );

      default:
        return null;
    }
  }

  #extractContainerSlotEditorPointer(
    editorPointer: EditorPointer<CompRef>,
    context: SlotContext,
  ): EditorPointer<CompRef> | null {
    const { dataDefinitions, dataItemKeyInfo } = context;

    if (!canResolvePathPrefix(dataDefinitions, dataItemKeyInfo.parts)) {
      return null;
    }

    const componentSlots = this.#getComponentSlots(editorPointer);
    return this.#buildEditorPointer(componentSlots[dataItemKeyInfo.leafKey]);
  }

  #extractArrayItemsSlotEditorPointer(
    editorPointer: EditorPointer<CompRef>,
    editorElement: EditorElement | InlineElement,
    dataItemKeyInfo: DataItemKeyInfo,
  ): EditorPointer<CompRef> | null {
    const componentSlots = this.#getComponentSlots(editorPointer);
    const fallbackSlot = componentSlots[dataItemKeyInfo.leafKey];
    const data = this.#getData(editorPointer);
    const arrayItemsIndex = this.#getArrayItemsSelectedIndex(
      editorPointer,
      editorElement,
    );

    if (typeof arrayItemsIndex !== 'number') {
      return this.#buildEditorPointer(fallbackSlot);
    }

    const arrayItemsData = getArrayItemsData(data, dataItemKeyInfo.rootKey);
    const activeArrayItem = arrayItemsData[arrayItemsIndex];
    const activeSlotName = get(activeArrayItem, dataItemKeyInfo.remainingParts);
    const activeSlot =
      activeSlotName != null ? componentSlots[String(activeSlotName)] : null;

    return this.#buildEditorPointer(activeSlot ?? fallbackSlot);
  }

  #extractBuilderSlotEditorPointer(
    editorPointer: EditorPointer<CompRef>,
    editorElement: EditorElement | InlineElement,
    request: SlotContext,
  ): EditorPointer<CompRef> | null {
    const matchingSlots = getMatchingSlotsByDataItemKey(
      this.#getBuilderSlots(editorPointer),
      request.dataItemKeyInfo,
    );

    if (matchingSlots.length === 0) {
      return null;
    }

    switch (request.rootDataItem.dataType) {
      case DataType.container:
        return this.#buildEditorPointerFromBuilderSlot(last(matchingSlots));

      case DataType.arrayItems:
        return this.#buildEditorPointerFromBuilderSlot(
          this.#pickActiveArrayItemSlot(
            editorPointer,
            editorElement,
            request.dataItemKeyInfo,
            matchingSlots,
          ),
        );

      default:
        return null;
    }
  }

  #pickActiveArrayItemSlot(
    editorPointer: EditorPointer<CompRef>,
    editorElement: EditorElement | InlineElement,
    dataItemKeyInfo: DataItemKeyInfo,
    matchingSlots: readonly PopulatedComponentContainerInfo[],
  ): PopulatedComponentContainerInfo | null {
    if (dataItemKeyInfo.remainingParts.length === 0) {
      return null;
    }

    const data = this.#getData(editorPointer);
    const arrayItemsIndex = this.#getArrayItemsSelectedIndex(
      editorPointer,
      editorElement,
    );

    const arrayItemsData =
      typeof arrayItemsIndex === 'number'
        ? getArrayItemsData(data, dataItemKeyInfo.rootKey)
        : [];
    const activeSlot = pickActiveArrayItemSlot(
      matchingSlots,
      dataItemKeyInfo.rootKey,
      arrayItemsIndex,
      arrayItemsData,
    );

    if (activeSlot) {
      return activeSlot;
    }

    return null;
  }

  #getArrayItemsSelectedIndex(
    editorPointer: EditorPointer<CompRef>,
    editorElement: EditorElement | InlineElement,
  ) {
    const cachedArrayItemsIndex = getCachedArrayItemIndex(
      this.editorPointerAPI,
      editorPointer,
    );

    if (cachedArrayItemsIndex !== null) {
      return cachedArrayItemsIndex;
    }

    if (!editorElement.displayGroups) {
      return null;
    }

    const indexKey = findArrayItemsSelectedIndexKey(
      editorElement.displayGroups,
    );

    if (!indexKey) {
      return null;
    }
    const data = this.#getData(editorPointer);

    const selectedArrayIndex = data[indexKey];
    if (selectedArrayIndex != null) {
      return selectedArrayIndex;
    }

    /**
     * NOTE: currently the default value for array index
     * for merging slot and container manifests
     * is taken right from data item definitions, since
     * we cannot query and resolve defaultValue from ManifestResolver,
     * since ManifestResolver is not accessible here
     */
    return editorElement?.data?.[indexKey]?.defaultValue ?? null;
  }

  #hasComponentSlots(documentPointer: CompRef): boolean {
    const getSlotNames = this.dependenciesContributor.get(
      DependencyToken.COMPONENTS_SLOTS_GET_NAMES,
    );
    return getSlotNames(documentPointer).length > 0;
  }

  #shouldUseBuilderSlotsAPI(
    extensionType: ManifestComponentExtensionType | null,
  ): boolean {
    return (
      this.manifestServiceFlags.shouldUseBuilderSlotsAPI &&
      extensionType === ManifestComponentExtensionType.BUILDER
    );
  }

  #getDocumentPointer(editorPointer: EditorPointer<CompRef>): CompRef {
    return this.editorPointerAPI.getDocumentPointer(editorPointer);
  }

  #getComponentSlots(editorPointer: EditorPointer<CompRef>) {
    const getSlotsData = this.dependenciesContributor.get(
      DependencyToken.COMPONENTS_SLOTS_GET_DATA,
    );

    return getSlotsData(this.#getDocumentPointer(editorPointer));
  }

  #getBuilderSlots(editorPointer: EditorPointer<CompRef>) {
    const getBuilderSlotsData = this.dependenciesContributor.get(
      DependencyToken.COMPONENTS_SLOTS_BUILDER_GET_DATA,
    );

    return getBuilderSlotsData(this.#getDocumentPointer(editorPointer));
  }

  #getData(editorPointer: EditorPointer<CompRef>) {
    const getData = this.dependenciesContributor.get(
      DependencyToken.BUILDER_DATA_GET,
    );

    return getData(editorPointer) ?? {};
  }

  #buildEditorPointer(
    documentPointer?: CompRef | null,
  ): EditorPointer<CompRef> | null {
    return documentPointer
      ? this.editorPointerAPI.buildEditorPointer(documentPointer)
      : null;
  }

  #buildEditorPointerFromBuilderSlot(
    slot?: PopulatedComponentContainerInfo | null,
  ): EditorPointer<CompRef> | null {
    return this.#buildEditorPointer(slot?.compRef);
  }

  override withEditorPointer(editorPointer: EditorPointer<CompRef>) {
    if (!this.manifestServiceFlags.shouldMergeSlotManifests) {
      super.withEditorPointer(editorPointer);
      return this;
    }

    const resolvedEditorElement =
      this.manifestStore.getResolvedEditorElement(editorPointer);

    if (resolvedEditorElement) {
      const slotEditorPointer = this.extractSlotsEditorPointers(
        editorPointer,
        resolvedEditorElement,
      );

      if (slotEditorPointer) {
        const builder = new MergeVirtualManifestBuilderPure(
          this.manifestStore,
          this.dependenciesContributor,
          this.editorPointerAPI,
          this.manifestServiceFlags,
        );

        builder.withEditorElement(editorPointer, resolvedEditorElement);
        builder.withEditorPointer(slotEditorPointer);

        const virtualManifest = builder.build('slot-virtual-manifest-merged');

        this.withVirtualManifest({
          ...virtualManifest,
          // override layout, use the original, not merged
          tree: {
            ...virtualManifest.tree,
            manifest: {
              ...virtualManifest.tree.manifest,
              editorElement: {
                ...(virtualManifest.tree.manifest?.editorElement ?? {}),
                layout: (resolvedEditorElement as EditorElement).layout,
              },
            },
          },
          manifests: virtualManifest.manifests.map((x) => {
            if (x.editorPointer.asUniqueKey === editorPointer.asUniqueKey) {
              return x;
            }

            return {
              ...x,
              isAutoInjected: true,
            };
          }),
        });
      } else {
        this.withEditorElement(editorPointer, resolvedEditorElement);
      }
    } else {
      this.withEditorElement(editorPointer, {});
    }

    return this;
  }
}
